/* import __COLOCATED_TEMPLATE__ from './admin-record-payment-modal.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { SH_GENERAL_INVOICE_TRANSACTION } from 'sh-www/models/sh-general-invoice-transaction';
import { makeMoney } from 'sh-www/helpers/make-money';
import { clearErrors, handleErrorResponse } from 'sh-www/utils/handles-errors';
import { closeModal } from 'sh-www/utils/modal-controls';

const CLASS_NAME = 'SH-c-reports-admin-record-payment-modal';

const INLINE_ERRORS = [
  'generic-exception',
  'amount_in_cents',
  'payment_method',
  'transaction_date',
  'memo',
];

class ReportsAdminRecordPaymentModal extends Component {
  @service constants;
  @service moment;
  @service session;
  @service('commands/sh-general-invoice-commands') shGeneralInvoiceCommandService;

  @tracked errors = {};
  @tracked paymentAmount = '';
  @tracked paymentMethod = null;
  @tracked transactionDate = '';
  @tracked memo = '';

  get className() {
    return CLASS_NAME;
  }

  get inlineErrors() {
    return INLINE_ERRORS;
  }

  get maxDate() {
    if (this.args.model.maxDate) {
      return this.args.model.maxDate;
    }

    // end of day today
    const newDate = new Date();
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  }

  get paymentMethodOptions() {
    return Object.keys(SH_GENERAL_INVOICE_TRANSACTION.PAYMENT_METHODS).map((key) => {
      const paymentMethod = SH_GENERAL_INVOICE_TRANSACTION.PAYMENT_METHODS[key];
      return {
        key: SH_GENERAL_INVOICE_TRANSACTION.PAYMENT_METHOD_NAMES[paymentMethod],
        model: {
          id: paymentMethod,
          label: SH_GENERAL_INVOICE_TRANSACTION.PAYMENT_METHOD_NAMES[paymentMethod],
        },
      }
    });
  }

  get paymentAmountInUSDMax() {
    return Number.parseFloat((this.args.model.shGeneralInvoice.amountUnpaidInCents / 100).toFixed(2));
  }

  get paymentMethodValue() {
    return this.paymentMethod?.label ?? null;
  }

  get inputDisabled() {
    return this.recordPayment.isRunning;
  }

  get submitDisabled() {
    return !this.paymentMethod
      || !this.transactionDate
      || (
        !this.paymentAmount
        || Number.parseFloat(this.paymentAmount) <= 0
      );
  }

  @dropTask
  *recordPayment() {
    clearErrors(this);

    const {
      paymentMethod,
      paymentMethodValue,
      paymentAmount,
      transactionDate,
      memo,
    } = this;

    const {
      shGeneralInvoice,
      onComplete,
    } = this.args.model;

    const amountInCents = Number.parseFloat(paymentAmount) * 100;

    try {
      yield this.shGeneralInvoiceCommandService.storeShGeneralInvoiceTransaction({
        amountInCents,
        memo,
        transactionDate,
        paymentMethod: paymentMethod.id,
        shGeneralInvoiceId: shGeneralInvoice.id,
      });
    } catch (e) {
      handleErrorResponse(this, e, INLINE_ERRORS, true, true);
      return;
    }

    yield onComplete({ paymentAmount, paymentMethodValue });
  }

  @action
  handlePaymentMethodInput(method) {
    this.paymentMethod = method;
  }

  @action
  handlePaymentAmountInput(amount) {
    const { paymentAmountInUSDMax } = this;
    if (amount > paymentAmountInUSDMax) {
      this.paymentAmount = paymentAmountInUSDMax;
    } else {
      this.paymentAmount = amount;
    }
  }

  @action
  handleMemoInput(value) {
    this.memo = value;
  }

  @action
  handlePaymentAmountBlur() {
    const { paymentAmount } = this;
    const paymentAmountFloat = Number.parseFloat(paymentAmount);

    // clear bad input
    if (!Number.isFinite(paymentAmountFloat) || paymentAmountFloat <= 0) {
      this.paymentAmount = '';
    } else {
      this.paymentAmount = makeMoney([paymentAmount], { commas: false }).replace('$', '');
    }
  }

  @action
  handleDateInput(value) {
    this.transactionDate = value;
  }

  @action
  handleDismiss() {
    closeModal(this);
  }

  /**
   * Handle Pikaday's accessability keybinds
   *
   * @param {number} index
   * @param {Event} e
   */
  @action
  handleDateKeyDown(e) {
    if (e.defaultPrevented) {
      return;
    }

    switch (e.key) {
      case 'Backspace':
      case 'Delete':
        e.preventDefault();

        // fire update event since pikaday does not
        this.transactionDate = '';

        break;
    }
  }
}

export default ReportsAdminRecordPaymentModal;
